import { DateRange } from '@/components/DateRangePicker';
import {
    AGENT_TYPE,
    AgentConversation,
    EVENT_TYPES, MessageType, ROLES, STATUSES
} from '@common/api/agentService';
import { supabase } from '@common/api/authService';
import dayjs from 'dayjs';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef, useState
} from 'react';

const DISABLE_WORKSPACE_DASHBOARD_API = true;
const DISABLE_WORKSPACE_DASHBOARD_CONVERSATIONS_API = true;

// Types
export interface KPI {
  id?: string;
  layout?: { x: number; y: number; w: number; h: number };
  title: string;
  sql: string;
  sql_detail?: string;
  formula: string;
  column: string;
  format: 'number' | 'currency' | 'percent' | 'string';
  unit: string;
  data: DataObj;
  prevData?: DataObj;
  loading?: boolean;
  sources?: string[]; // 数据源列表,如 ['Shopify', 'Google Ads']
  trend_sql?: string; // 趋势数据 SQL
  raw_data_sql?: string; // 原始数据 SQL
}

export type DataObj = Record<string, (number | string)>[];

export interface ChartConfig {
  cartesian?: {
      xAxis?: {
          field?: string;
          type?: 'category' | 'value' | 'time' | 'log';
          name?: string;
          axisLabel?: {
              formatter?: string;
          };
      };
      yAxis?: {
          type?: 'value' | 'category' | 'log';
          name?: string;
          axisLabel?: {
              formatter?: string;
          };
      }[];
      series?: {
          type: 'line' | 'bar' | 'scatter' | 'heatmap';
          name?: string;
          encode?: {
              x?: string;
              y?: string;
              value?: string;
          };
          yAxisIndex?: number;
          stack?: string;
          label?: {
              formatter?: string;
          };
      }[];
  };
  pie?: {
      series?: {
          type: 'pie';
          name?: string;
          encode?: {
              itemName?: string;
              value?: string;
          };
          label?: {
              formatter?: string;
          };
      }[];
  };
  radar?: {
      indicator?: { name: string; max: number; field?: string }[];
      series?: {
          type: 'radar';
          name?: string;
          encode?: {
              itemName?: string;
          };
      }[];
  };
  funnel?: {
      series?: {
          type: 'funnel';
          name?: string;
          encode?: {
              itemName?: string;
              value?: string;
          };
          label?: {
              formatter?: string;
          };
      }[];
  };
  gauge?: {
      series?: {
          type: 'gauge';
          name?: string;
          encode?: {
              value?: string;
          };
          axisLabel?: {
              formatter?: string;
          };
      }[];
  };
  sankey?: {
      series?: {
          type: 'sankey';
          name?: string;
          encode?: {
              source?: string;
              target?: string;
              value?: string;
          };
      }[];
  };
}

export interface Chart {
  id: string;
  layout?: { x: number; y: number; w: number; h: number };
  chart_config: ChartConfig;
  sql_detail?: string;
  title: string;
  data: DataObj; // dataset path or inline data
  sql: string;
  formula: string;
  loading?: boolean;
  sources?: string[]; // 数据源列表,如 ['Shopify', 'Google Ads']
  raw_data_sql?: string; // 原始数据 SQL
}

export interface Group {
  id: string;
  title: string;
  items_id: string[]; // Array of KPI and Chart IDs
  layout?: { x: number; y: number; w: number; h: number };
}

export interface DashboardSchema {
  kpi?: KPI[];
  chart?: Chart[];
  group?: Group[];
}

export type OperationType = 'ADD' | 'DELETE' | 'UPDATE' | 'IMPORT';

export interface PatchOperation {
    op: OperationType;
    path: string; // e.g. "kpi[0].layout.x" or "chart"
    value?: any;
}

export type DashboardActionType = 'BATCH_PATCH';

export interface DashboardAction {
    type: DashboardActionType;
    payload: PatchOperation[];
    description: string;
    timestamp: number;
    skipUndo?: boolean; // 标记是否应该在撤销时跳过（用于系统自动调整的操作）
}

export interface EditorState {
    originalSchema: DashboardSchema;
    currentSchema: DashboardSchema;
    actions: DashboardAction[];
    pointer: number; // Index of the last applied action
    isDirty: boolean;
}

export interface Dashboard {
    id?: string;
    title: string;
    schema: DashboardSchema;
    created_at: string;
    dateRange: DateRange;
    conversation_id?: string;
    sort_key?: number;
}

export interface DashboardState {
    /** 消息管理 */
    messages: MessageType[];
    setMessages: React.Dispatch<React.SetStateAction<MessageType[]>>;
    addMessage: (message: MessageType) => void;

    /** 生成状态 */
    isGenerating: boolean;
    setIsGenerating: React.Dispatch<React.SetStateAction<boolean>>;

    showGenerating: boolean;
    setShowGenerating: React.Dispatch<React.SetStateAction<boolean>>;

    /** AbortController 相关 */
    abortControllerRef: React.RefObject<AbortController | null>;
    cancelCurrentRequest: () => void;
    createNewAbortController: () => AbortController;

    /** 错误处理 */
    error: string | null;
    setError: React.Dispatch<React.SetStateAction<string | null>>;
    
    /** 版本管理 */
    history: MessageType[];
    setHistory: React.Dispatch<React.SetStateAction<MessageType[]>>;
    selectedVersion: Dashboard | null;
    setSelectedVersion: React.Dispatch<React.SetStateAction<Dashboard | null>>;
    versions: Dashboard[];
    setVersions: React.Dispatch<React.SetStateAction<Dashboard[]>>;
    isLoadingVersions: boolean;
    setIsLoadingVersions: React.Dispatch<React.SetStateAction<boolean>>;

    /** Editor State Management */
    editorStates: Record<string, EditorState>;
    initEditor: (dashboardId: string, schema: DashboardSchema) => void;
    dispatchAction: (dashboardId: string, action: Omit<DashboardAction, 'timestamp'>) => void;
    updateData: (dashboardId: string, schema: DashboardSchema) => void;
    undo: (dashboardId: string) => void;
    redo: (dashboardId: string) => void;
    saveDashboard: (dashboardId: string, title?: string, conversationId?: string) => Promise<void>;
    resetEditor: (dashboardId: string) => void;
    deleteDashboard: (dashboardId: string) => Promise<void>;
    renameDashboard: (id: string, newName: string) => Promise<void>;
    reorderDashboards: (newOrder: Dashboard[], movedId?: string, newIndex?: number) => Promise<void>;

    /** 创建模态框 */
    showCreateModal: boolean;
    setShowCreateModal: (show: boolean) => void;

    /** 拉取 dashboard 列表 */
    getDashboards: () => Promise<void>;

    /** 会话列表管理 */
    conversations: AgentConversation[];
    setConversations: React.Dispatch<React.SetStateAction<AgentConversation[]>>;
    currentConversationId: string | null;
    setCurrentConversationId: React.Dispatch<React.SetStateAction<string | null>>;
    isLoadingConversations: boolean;
    isLoadingMessages: boolean;
    getConversations: (dashboardId?: string) => Promise<void>;
    deleteConversation: (id: string) => Promise<void>;
    updateConversation: (id: string, title: string) => Promise<void>;
    getMessages: (conversationId: string) => Promise<void>;

    /** UI 状态 */
    isEditMode: boolean;
    setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;

    localSchema: DashboardSchema | undefined;
    setLocalSchema: React.Dispatch<React.SetStateAction<DashboardSchema | undefined>>;

    /** 全局日期范围 */
    globalDateRange: DateRange | undefined;
    setGlobalDateRange: React.Dispatch<React.SetStateAction<DateRange | undefined>>;

    /** 自动滚动 */
    activeItemId: string | null;
    setActiveItemId: React.Dispatch<React.SetStateAction<string | null>>;
}

const initialState: DashboardState = {
    messages: [],
    setMessages: () => {},
    addMessage: () => {},
    isGenerating: false,
    setIsGenerating: () => {},
    showGenerating: false,
    setShowGenerating: () => {},
    abortControllerRef: { current: null },
    cancelCurrentRequest: () => {},
    createNewAbortController: () => new AbortController(),
    error: null,
    setError: () => {},
    selectedVersion: null,
    setSelectedVersion: () => {},
    history: [],
    setHistory: () => {},
    versions: [],
    setVersions: () => {},
    isLoadingVersions: false,
    setIsLoadingVersions: () => {},
    
    editorStates: {},
    initEditor: () => {},
    dispatchAction: () => {},
    updateData: () => {},
    undo: () => {},
    redo: () => {},
    saveDashboard: async (_dashboardId: string, _title?: string, _conversationId?: string) => {},
    resetEditor: () => {},
    deleteDashboard: async () => {},
    renameDashboard: async () => {},
    reorderDashboards: async () => {},

    showCreateModal: false,
    setShowCreateModal: () => {},
    getDashboards: async () => {},

    conversations: [],
    setConversations: () => {},
    currentConversationId: null,
    setCurrentConversationId: () => {},
    isLoadingConversations: false,
    isLoadingMessages: false,
    getConversations: async (_dashboardId?: string) => {},
    deleteConversation: async () => {},
    updateConversation: async () => {},
    getMessages: async () => {},

    isEditMode: false,
    setIsEditMode: () => {},

    localSchema: undefined,
    setLocalSchema: () => {},

    globalDateRange: undefined,
    setGlobalDateRange: () => {},

    activeItemId: null,
    setActiveItemId: () => {},
};

export { initialState as dashboardInitialState };

const DashboardContext = createContext<DashboardState>(initialState);

export const useDashboardContext = () => useContext(DashboardContext);

// Minimal lodash replacement for avoiding type issues
const _get = (obj: any, path: string, defaultValue?: any) => {
    const keys = path.split(/[\.\[\]]/).filter(Boolean);
    let result = obj;
    for (const key of keys) {
        if (result === undefined || result === null) return defaultValue;
        result = result[key];
    }
    return result === undefined ? defaultValue : result;
};

const _set = (obj: any, path: string, value: any) => {
    const keys = path ? path.split(/[\.\[\]]/).filter(Boolean) : [];
    
    if (keys.length === 0) {
        if (obj && typeof obj === 'object') {
            if (Array.isArray(obj)) {
                obj.length = 0;
            } else {
                for (const key in obj) {
                    if (Object.prototype.hasOwnProperty.call(obj, key)) {
                        delete obj[key];
                    }
                }
            }
            
            if (value && typeof value === 'object') {
                Object.assign(obj, value);
            }
        }
        return;
    }

    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (current[key] === undefined || current[key] === null) {
            // Check if next key is an integer to decide array vs object
            current[key] = /^\d+$/.test(keys[i + 1]) ? [] : {};
        }
        current = current[key];
    }
    current[keys[keys.length - 1]] = value;
};

const _unset = (obj: any, path: string) => {
    if (!path) return;
    const keys = path.split(/[\.\[\]]/).filter(Boolean);
    if (keys.length === 0) return;

    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (current[key] === undefined || current[key] === null) return;
        current = current[key];
    }
    const lastKey = keys[keys.length - 1];
    if (Array.isArray(current)) {
        // Note: delete on array leaves hole, splice is better but unset is generic
        // We handle splice in applyPatch
        current.splice(parseInt(lastKey, 10), 1);
    } else {
        delete current[lastKey];
    }
};

// Helper to apply patches
const applyPatch = (obj: any, operation: PatchOperation) => {
    const {
        op, path, value 
    } = operation;
    
    // ADD: Can be array push OR object set key (if path ends in key)
    // If path targets an array directly (e.g. "kpi"), we assume PUSH.
    // If path targets a non-existent key, we SET it.
    if (op === 'ADD') {
        // Check if we are targeting an array itself
        const target = _get(obj, path);
        if (Array.isArray(target)) {
            target.push(value);
            return;
        }
        
        // Otherwise, it's setting a value (like adding a property or item to array via index)
        // If it's an array push via index (rare in patches, usually we push to array path),
        // we handle standard set.
        _set(obj, path, value);
        return;
    }

    if (op === 'DELETE') {
        // 解析路径，判断是否是数组索引删除
        const pathParts = path.match(/^(\w+)\[(\d+)\]$/);

        if (pathParts) {
            // 是数组索引删除 (如 "group[1]")
            const [, arrayKey, indexStr] = pathParts;
            const index = parseInt(indexStr, 10);
            const arr = obj[arrayKey];

            if (Array.isArray(arr) && index >= 0 && index < arr.length) {
                // 使用 splice 真正移除元素
                arr.splice(index, 1);
            }
        } else {
            // 对象属性删除
            _unset(obj, path);
        }
        return;
    }

    if (op === 'UPDATE' || op === 'IMPORT') {
        _set(obj, path, value);
    }
};

const applyActionToSchema = (schema: DashboardSchema, action: DashboardAction): DashboardSchema => {
    // Deep clone to ensure immutability
    const newSchema = JSON.parse(JSON.stringify(schema || {
        kpi: [], chart: [], group: [] 
    })) as DashboardSchema;
    if (!newSchema.kpi) newSchema.kpi = [];
    if (!newSchema.chart) newSchema.chart = [];
    if (!newSchema.group) {
        newSchema.group = [];
    } else if (!Array.isArray(newSchema.group)) {
        // Fix dirty state where group is an object
        newSchema.group = [newSchema.group];
    }

    if (action.type === 'BATCH_PATCH') {
        action.payload.forEach(op => {
            applyPatch(newSchema, op);
        });
    }
    
    return newSchema;
};

// Helper to preserve data fields from current schema to new schema during undo
const preserveData = (newSchema: DashboardSchema, currentSchema: DashboardSchema) => {
    if (!currentSchema || !newSchema) return newSchema;

    // Helper to sync item data
    const syncItemData = (newItem: any, currentItem: any) => {
        if (!newItem || !currentItem) return;
        // Preserve data-related fields that are not typically tracked by actions
        if (currentItem.data) newItem.data = currentItem.data;
        if (currentItem.prevData) newItem.prevData = currentItem.prevData;
        if (currentItem.sources) newItem.sources = currentItem.sources;
        if (currentItem.loading !== undefined) newItem.loading = currentItem.loading;
        // Do NOT preserve SQL or other config fields as they might be the target of undo
    };

    // Sync KPIs
    if (newSchema.kpi && currentSchema.kpi) {
        newSchema.kpi.forEach(newKpi => {
            const currentKpi = currentSchema.kpi?.find(k => k.id === newKpi.id);
            if (currentKpi) {
                syncItemData(newKpi, currentKpi);
            }
        });
    }

    // Sync Charts
    if (newSchema.chart && currentSchema.chart) {
        newSchema.chart.forEach(newChart => {
            const currentChart = currentSchema.chart?.find(c => c.id === newChart.id);
            if (currentChart) {
                syncItemData(newChart, currentChart);
            }
        });
    }
    
    return newSchema;
};

export const createDashboardProvider = (workspaceId?: string) => {
    const [messages, setMessages] = useState<MessageType[]>([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedVersion, setSelectedVersion] = useState<Dashboard | null>(null);
    const [history, setHistory] = useState<MessageType[]>([]);
    const [versions, setVersions] = useState<Dashboard[]>([]);
    const [isLoadingVersions, setIsLoadingVersions] = useState(false);
    const [showGenerating, setShowGenerating] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);
    const createNewAbortController = useCallback(() => {
        const controller = new AbortController();
        abortControllerRef.current = controller;
        return controller;
    }, []);

    const [conversations, setConversations] = useState<AgentConversation[]>([]);
    const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
    const [isLoadingConversations, setIsLoadingConversations] = useState(false);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [localSchema, setLocalSchema] = useState<DashboardSchema | undefined>();

    // 默认最近30天
    const [globalDateRange, setGlobalDateRange] = useState<DateRange | undefined>({
        startDate: dayjs().subtract(30, 'day').toDate(),
        endDate: dayjs().toDate(),
    });

    const [activeItemId, setActiveItemId] = useState<string | null>(null);

    // Batch delete queue to handle multiple deletes in stream
    const pendingDeleteQueue = useRef<Map<string, Array<{ id: string; type: 'kpi' | 'chart' | 'group' }>>>(new Map());
    const deleteTimerRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

    // Editor State
    const [editorStates, setEditorStates] = useState<Record<string, EditorState>>({});
    const editorStatesRef = useRef(editorStates);
    useEffect(() => {
        editorStatesRef.current = editorStates;
    }, [editorStates]);

    const initEditor = useCallback((dashboardId: string, schema: DashboardSchema) => {
        setEditorStates(prev => {
            // Always allow re-init if we want to reset? No, only if not exists.
            // If we switch back to a dashboard, we want to keep state.
            if (prev[dashboardId]) {
                return prev;
            }
            return {
                ...prev,
                [dashboardId]: {
                    originalSchema: schema,
                    currentSchema: schema,
                    actions: [],
                    pointer: -1,
                    isDirty: false
                }
            };
        });
    }, [editorStates]);

    const updateData = useCallback((dashboardId: string, schema: DashboardSchema) => {
        const oldCount = editorStates[dashboardId]?.currentSchema?.kpi?.length;
        setEditorStates(prev => {
            const state = prev[dashboardId];
            if (!state) {
                console.warn('updateData - state not found for dashboardId:', dashboardId);
                return prev;
            }

            return {
                ...prev,
                [dashboardId]: {
                    ...state,
                    currentSchema: schema
                }
            };
        });
    }, [editorStates]);

    const dispatchAction = useCallback((dashboardId: string, action: Omit<DashboardAction, 'timestamp'>) => {
        setEditorStates(prev => {
            const state = prev[dashboardId];
            if (!state) {
                console.warn('dispatchAction - state not found for dashboardId:', dashboardId);
                return prev;
            }

            const timestamp = Date.now();
            const lastAction = state.actions[state.pointer];

            console.log('dispatchAction - 添加新操作:', {
                dashboardId,
                action: {
                    description: action.description,
                    payloadCount: action.payload.length,
                    skipUndo: action.skipUndo,
                    ops: action.payload.map(p => ({ op: p.op, path: p.path }))
                },
                currentState: {
                    pointer: state.pointer,
                    totalActions: state.actions.length,
                    lastAction: lastAction ? {
                        description: lastAction.description,
                        payloadCount: lastAction.payload.length,
                        timestamp: lastAction.timestamp
                    } : null
                }
            });

            // Merge logic:
            // If the new action is a "Layout changed" event,
            // AND the last action was recent (e.g. < 2000ms),
            // AND the last action was a structural change (ADD or DELETE),
            // THEN merge this layout update into the last action.

            const isLayoutChange = action.description === 'Layout changed';
            let shouldMerge = false;

            if (isLayoutChange && lastAction) {
                const timeDiff = timestamp - lastAction.timestamp;
                if (timeDiff < 2000) {
                    const hasStructuralChange = lastAction.payload.some(op => op.op === 'ADD' || op.op === 'DELETE');
                    if (hasStructuralChange) {
                        shouldMerge = true;
                        console.log('dispatchAction - 合并操作:', {
                            reason: 'Layout changed 在结构性操作后 2 秒内',
                            timeDiff,
                            lastActionPayloadCount: lastAction.payload.length,
                            newPayloadCount: action.payload.length,
                            mergedPayloadCount: lastAction.payload.length + action.payload.length
                        });
                    }
                }
            }

            if (shouldMerge) {
                // 合并时需要对相同 path 的操作进行去重，保留最新的操作
                const mergedPayload = [...lastAction.payload];
                const newPayload = action.payload;

                // 使用 Map 来去重，key 为 path，value 为操作
                const payloadMap = new Map<string, PatchOperation>();

                // 先添加旧的操作
                mergedPayload.forEach(op => {
                    payloadMap.set(op.path, op);
                });

                // 新操作覆盖旧操作
                newPayload.forEach(op => {
                    payloadMap.set(op.path, op);
                });

                // 转换回数组
                const dedupedPayload = Array.from(payloadMap.values());

                const mergedAction: DashboardAction = {
                    ...lastAction,
                    payload: dedupedPayload,
                    timestamp: timestamp // Update timestamp to extend the window if needed
                };

                const newActions = [...state.actions];
                // Since we are at state.pointer (tip of effective history for this merge), update it
                newActions[state.pointer] = mergedAction;

                // Apply ONLY the NEW operations to the current schema
                const newSchema = applyActionToSchema(state.currentSchema, { ...action, timestamp } as DashboardAction);

                console.log('dispatchAction - 合并后:', {
                    pointer: state.pointer,
                    originalPayloadCount: lastAction.payload.length,
                    newPayloadCount: action.payload.length,
                    dedupedPayloadCount: dedupedPayload.length,
                    removedCount: lastAction.payload.length + action.payload.length - dedupedPayload.length
                });

                return {
                    ...prev,
                    [dashboardId]: {
                        ...state,
                        actions: newActions,
                        currentSchema: newSchema,
                        isDirty: true
                    }
                };
            }

            const newAction = { ...action, timestamp: timestamp };
            // Truncate history if we are in the middle
            const newActions = state.actions.slice(0, state.pointer + 1);
            newActions.push(newAction);

            const newSchema = applyActionToSchema(state.currentSchema, newAction);

            console.log('dispatchAction - 添加新 action:', {
                newPointer: newActions.length - 1,
                totalActions: newActions.length
            });

            return {
                ...prev,
                [dashboardId]: {
                    ...state,
                    actions: newActions,
                    pointer: newActions.length - 1,
                    currentSchema: newSchema,
                    isDirty: true
                }
            };
        });
    }, []);

    const undo = useCallback((dashboardId: string) => {
        setEditorStates(prev => {
            const state = prev[dashboardId];
            if (!state || state.pointer < 0) {
                console.log('撤销 - 无法撤销: state不存在或pointer < 0', { dashboardId, pointer: state?.pointer });
                return prev;
            }

            console.log('撤销 - 当前改动列表:', {
                dashboardId,
                pointer: state.pointer,
                totalActions: state.actions.length,
                allActions: state.actions.map((a, i) => ({
                    index: i,
                    description: a.description,
                    payloadCount: a.payload.length,
                    skipUndo: a.skipUndo,
                    ops: a.payload.map(p => ({ op: p.op, path: p.path }))
                }))
            });

            // 从当前 pointer 向前查找，跳过所有 skipUndo 的操作
            // 找到第一个非 skipUndo 的操作，撤销它
            let targetPointer = state.pointer;

            // 跳过当前及所有连续的 skipUndo 操作
            while (targetPointer >= 0) {
                const action = state.actions[targetPointer];
                const shouldSkip = action?.skipUndo
                    || (action?.payload?.length > 0 && action?.payload.every(op => op.op === 'IMPORT'));

                if (!shouldSkip) {
                    // 找到了第一个非 skipUndo 的操作
                    break;
                }

                console.log('撤销 - 跳过 skipUndo 操作:', {
                    pointer: targetPointer,
                    description: action?.description,
                    reason: action?.skipUndo ? '标记为 skipUndo' : '全部是 IMPORT'
                });

                targetPointer--;
            }

            // 如果所有操作都是 skipUndo，无法撤销
            if (targetPointer < 0) {
                console.log('撤销 - 没有可撤销的用户操作');
                return {
                    ...prev,
                    [dashboardId]: {
                        ...state,
                        pointer: -1,
                        currentSchema: JSON.parse(JSON.stringify(state.originalSchema)),
                        isDirty: false
                    }
                };
            }

            // 撤销找到的第一个非 skipUndo 操作
            const newPointer = targetPointer - 1;
            console.log('撤销 - 撤销操作:', {
                undoPointer: targetPointer,
                newPointer,
                description: state.actions[targetPointer].description
            });

            // 重新应用从 0 到 newPointer 的所有操作
            let schema = JSON.parse(JSON.stringify(state.originalSchema));
            for (let i = 0; i <= newPointer; i++) {
                schema = applyActionToSchema(schema, state.actions[i]);
            }

            // 修复：保留当前 schema 中的数据状态
            schema = preserveData(schema, state.currentSchema);

            return {
                ...prev,
                [dashboardId]: {
                    ...state,
                    pointer: newPointer,
                    currentSchema: schema,
                    isDirty: newPointer >= -1
                }
            };
        });
    }, []);

    const redo = useCallback((dashboardId: string) => {
        setEditorStates(prev => {
            const state = prev[dashboardId];
            if (!state || state.pointer >= state.actions.length - 1) return prev;

            const newPointer = state.pointer + 1;
            const action = state.actions[newPointer];
            const schema = applyActionToSchema(state.currentSchema, action);

            return {
                ...prev,
                [dashboardId]: {
                    ...state,
                    pointer: newPointer,
                    currentSchema: schema,
                    isDirty: true
                }
            };
        });
    }, []);

    const resetEditor = useCallback((dashboardId: string) => {
        setEditorStates(prev => {
            const state = prev[dashboardId];
            if (!state) return prev;
            return {
                ...prev,
                [dashboardId]: {
                    ...state,
                    currentSchema: state.originalSchema,
                    actions: [],
                    pointer: -1,
                    isDirty: false
                }
            };
        });
    }, []);

    const saveDashboard = useCallback(async (dashboardId: string, title?: string, conversationId?: string) => {
        if (DISABLE_WORKSPACE_DASHBOARD_API) {
            // workspace_dashboard/workspace_dashboard_gen_conversations APIs are being sunset.
            // Keep function shape and return without remote persistence.
            return dashboardId;
        }
        const state = editorStates[dashboardId];
        // 如果是临时dashboard，可能没有dirty状态（如果是刚初始化的），但我们也应该允许保存（创建）
        const isPersisted = versions.some(v => v.id === dashboardId);

        // 检查是否需要保存：要么是新的dashboard，要么schema有变化，要么标题有变化
        const currentDashboard = versions.find(v => v.id === dashboardId);
        const hasTitleChange = title && currentDashboard && title !== currentDashboard.title;
        if (isPersisted && (!state || !state.isDirty) && !hasTitleChange) return;

        const currentSchema = state?.currentSchema || versions.find(v => v.id === dashboardId)?.schema;

        try {
            if (!isPersisted) {
                // Create new dashboard
                const sortKey = Date.now();
                const newTitle = title || 'New Dashboard';

                const { data, error } = await supabase
                    .from('workspace_dashboard')
                    .insert([{
                        workspace_id: workspaceId,
                        title: newTitle,
                        schema: currentSchema || {
                            kpi: [], chart: [], group: [] 
                        },
                        sort_key: sortKey
                    }])
                    .select()
                    .single();

                if (error) throw error;

                // 关联会话到仪表板
                if (conversationId) {
                    const { error: convError } = await supabase
                        .from('workspace_dashboard_gen_conversations')
                        .upsert({
                            dashboard_id: data.id,
                            id: conversationId,
                            workspace_id: workspaceId
                        }, { onConflict: 'id' });

                    if (convError) {
                        console.error("Failed to link conversation to dashboard:", convError);
                    }
                }

                const newDashboard: Dashboard = {
                    id: data.id,
                    title: data.title,
                    schema: typeof data.schema === 'string' ? JSON.parse(data.schema) : data.schema,
                    created_at: data.created_at,
                    dateRange: {
                        startDate: dayjs().subtract(30, 'day').toDate(),
                        endDate: dayjs().toDate(),
                    }
                };

                // Update versions: replace temp dashboard with new one (actually we should add it if it wasn't there? No, we likely added it to versions manually in UI?)
                // Wait, in previous logic, we did NOT add temp dashboard to `versions`.
                // So here we should ADD it to versions.
                // BUT, if we used UUID for temp ID, we might want to REPLACE if we had put it in versions?
                // The current implementation of `selectTemplate` sets `selectedVersion` but does NOT update `versions`.
                // So `isPersisted` check (versions.some) is correct: it will be false for temp dashboard.

                setVersions(prev => [newDashboard, ...prev]);

                // Update selectedVersion
                setSelectedVersion(newDashboard);

                // Update editorStates: migrate state from temp ID to new ID
                setEditorStates(prev => {
                    const newState = { ...prev };
                    if (newState[dashboardId]) {
                        newState[data.id] = {
                            ...newState[dashboardId],
                            originalSchema: newDashboard.schema, // Reset original to current
                            actions: [],
                            pointer: -1,
                            isDirty: false
                        };
                        delete newState[dashboardId];
                    }
                    return newState;
                });

                return data.id;

            } else {
                // Update existing dashboard
                const updates: any = {};
                if (state && state.isDirty) {
                    updates.schema = state.currentSchema;
                }
                if (title) {
                    updates.title = title;
                }

                if (Object.keys(updates).length === 0) return;

                const { error } = await supabase
                    .from('workspace_dashboard')
                    .update(updates)
                    .eq('id', dashboardId);

                if (error) throw error;

                // 关联会话到仪表板（更新时也可以关联）
                if (conversationId) {
                    const { error: convError } = await supabase
                        .from('workspace_dashboard_gen_conversations')
                        .upsert({
                            dashboard_id: dashboardId,
                            id: conversationId,
                            workspace_id: workspaceId
                        }, { onConflict: 'id' });

                    if (convError) {
                        console.error("Failed to link conversation to dashboard:", convError);
                    }
                }

                const newSchema = updates.schema || state?.currentSchema;

                setEditorStates(prev => {
                    if (!prev[dashboardId]) return prev;
                    return {
                        ...prev,
                        [dashboardId]: {
                            ...prev[dashboardId],
                            originalSchema: newSchema,
                            actions: [],
                            pointer: -1,
                            isDirty: false
                        }
                    };
                });

                setVersions(prev => prev.map(v =>
                    v.id === dashboardId ? { ...v, ...updates } : v));

                setSelectedVersion(prev => {
                    if (prev && prev.id === dashboardId) {
                        return { ...prev, ...updates };
                    }
                    return prev;
                });
            }

        } catch (e) {
            console.error("Failed to save dashboard:", e);
            setError("Failed to save changes");
        }
    }, [editorStates, versions, workspaceId]);

    // Realtime subscription for conversations
    useEffect(() => {
        if (!workspaceId) return;
        if (DISABLE_WORKSPACE_DASHBOARD_CONVERSATIONS_API) {
            // workspace_dashboard_gen_conversations API is being sunset. Keep local state empty.
            setConversations([]);
            return;
        }
        let channel: any = null;

        const setupSubscription = async () => {

            channel = supabase
                .channel(`dashboard_conversations_${workspaceId}`)
                .on(
                    'postgres_changes',
                    {
                        event: '*',
                        schema: 'public',
                        table: 'workspace_dashboard_gen_conversations',
                        filter: `workspace_id=eq.${workspaceId}`
                    },
                    (payload) => {
                        if (payload.eventType === 'INSERT') {
                            const newConv = payload.new as AgentConversation;
                            setConversations((prev) => {
                                if (prev.some(c => c.id === newConv.id)) return prev;
                                return [newConv, ...prev];
                            });
                        } else if (payload.eventType === 'UPDATE') {
                            const updatedConv = payload.new as AgentConversation;
                            setConversations((prev) => 
                                prev.map((c) => (c.id === updatedConv.id ? updatedConv : c)));
                        } else if (payload.eventType === 'DELETE') {
                            const deletedId = payload.old.id;
                            setConversations((prev) => 
                                prev.filter((c) => c.id !== deletedId));
                        }
                    }
                )
                .subscribe();
        };

        setupSubscription();

        return () => {
            if (channel) {
                supabase.removeChannel(channel);
            }
        };
    }, [workspaceId]);

    const getConversations = useCallback(async (dashboardId?: string) => {
        if (DISABLE_WORKSPACE_DASHBOARD_CONVERSATIONS_API) {
            // workspace_dashboard_gen_conversations API is being sunset. Return empty list.
            setConversations([]);
            return;
        }
        setIsLoadingConversations(true);
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            let query = supabase
                .from('workspace_dashboard_gen_conversations')
                .select('*')
                .eq('user_id', user.id)
                .eq('workspace_id', workspaceId);

            // 如果提供了 dashboardId，只加载与该 dashboard 关联的 conversation
            if (dashboardId) {
                query = query.eq('dashboard_id', dashboardId);
            }

            const { data, error } = await query.order('updated_at', { ascending: false });

            if (error) throw error;
            setConversations(data || []);
        } catch (error) {
            console.error("Error fetching conversations:", error);
        } finally {
            setIsLoadingConversations(false);
        }
    }, [workspaceId]);

    const createConversation = useCallback(async (title: string = 'New Dashboard') => {
        if (DISABLE_WORKSPACE_DASHBOARD_CONVERSATIONS_API) {
            // workspace_dashboard_gen_conversations API is being sunset. Skip remote create.
            return null;
        }
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return null;

            const { data, error } = await supabase
                .from('workspace_dashboard_gen_conversations')
                .insert([{ title, user_id: user.id }])
                .eq('workspace_id', workspaceId)
                .select()
                .single();
            
            if (error) throw error;
            
            setConversations(prev => {
                if (prev.some(c => c.id === data.id)) return prev;
                return [data, ...prev];
            });
            setCurrentConversationId(data.id);
            setMessages([]);
            return data.id;
        } catch (error) {
            console.error("Error creating conversation:", error);
            return null;
        }
    }, [workspaceId]);

    const deleteConversation = useCallback(async (id: string) => {
        if (DISABLE_WORKSPACE_DASHBOARD_CONVERSATIONS_API) {
            // workspace_dashboard_gen_conversations API is being sunset. Keep local cleanup only.
            setConversations(prev => prev.filter(c => c.id !== id));
            if (currentConversationId === id) {
                setCurrentConversationId(null);
                setMessages([]);
            }
            return;
        }
        try {
            const { error } = await supabase
                .from('workspace_dashboard_gen_conversations')
                .delete()
                .eq('id', id);
            
            if (error) throw error;
            
            setConversations(prev => prev.filter(c => c.id !== id));
            if (currentConversationId === id) {
                setCurrentConversationId(null);
                setMessages([]);
            }
        } catch (error) {
            console.error("Error deleting conversation:", error);
        }
    }, [currentConversationId]);

    const updateConversation = useCallback(async (id: string, title: string) => {
        if (DISABLE_WORKSPACE_DASHBOARD_CONVERSATIONS_API) {
            // workspace_dashboard_gen_conversations API is being sunset. Keep local title update only.
            setConversations(prev => prev.map(c => c.id === id ? { ...c, title } : c));
            return;
        }
        try {
            const { error } = await supabase
                .from('workspace_dashboard_gen_conversations')
                .update({ title, updated_at: new Date().toISOString() })
                .eq('id', id);
            
            if (error) throw error;
            
            setConversations(prev => prev.map(c => c.id === id ? { ...c, title } : c));
        } catch (error) {
            console.error("Error updating conversation:", error);
        }
    }, []);

    const getMessages = useCallback(async (conversationId: string) => {
        if (!conversationId || conversationId === 'temp') return;
        setIsLoadingMessages(true);
        try {
            const { data, error } = await supabase
                .from('workspace_dashboard_gen_messages')
                .select('*')
                .eq('conversation_id', conversationId)
                .order('created_at', { ascending: true });
            
            if (error) throw error;
            
            // Parse raw_data which contains the array of messages for each run
            const allMessages: MessageType[] = [];
            
            data.forEach((row: any) => {
                try {
                    // raw_data is stored as JSONB, so it might already be an object/array in JS
                    // If it's a string, parse it. If it's already an object, use it.
                    let messages = row.raw_data;
                    if (typeof messages === 'string') {
                        messages = JSON.parse(messages);
                    }
                    
                    if (Array.isArray(messages)) {
                        allMessages.push(...messages);
                    } else if (messages && typeof messages === 'object') {
                        // If it's a single message object
                        allMessages.push(messages);
                    }
                } catch (e) {
                    console.error("Error parsing message raw_data:", e);
                }
            });
            
            // Sort by created_at just in case
            allMessages.sort((a, b) => {
                return new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
            });

            setMessages(allMessages);
        } catch (error) {
            console.error("Error fetching messages:", error);
        } finally {
            setIsLoadingMessages(false);
        }
    }, []);

    // 当切换 dashboard 时，自动加载关联的 conversation 和 messages
    useEffect(() => {
        const dashboardId = selectedVersion?.id;

        // 如果是临时 dashboard 或没有 dashboard，清空状态
        if (!dashboardId || dashboardId === 'temp') {
            setConversations([]);
            setCurrentConversationId(null);
            setMessages([]);
            return;
        }

        // 加载该 dashboard 关联的 conversations
        const loadDashboardConversations = async () => {
            if (DISABLE_WORKSPACE_DASHBOARD_CONVERSATIONS_API) {
                // workspace_dashboard_gen_conversations API is being sunset. Keep local conversation state empty.
                setConversations([]);
                setCurrentConversationId(null);
                setMessages([]);
                return;
            }
            setIsLoadingConversations(true);
            try {
                const { data: { user } } = await supabase.auth.getUser();
                if (!user) return;

                const { data, error } = await supabase
                    .from('workspace_dashboard_gen_conversations')
                    .select('*')
                    .eq('dashboard_id', dashboardId)
                    .eq('user_id', user.id)
                    .order('updated_at', { ascending: false });

                if (error) throw error;

                const conversations = data || [];
                setConversations(conversations);

                // 如果有 conversations，选中第一个并加载其消息
                if (conversations.length > 0) {
                    const firstConv = conversations[0];
                    setCurrentConversationId(firstConv.id);
                    // getMessages 会异步加载消息
                    await getMessages(firstConv.id);
                } else {
                    // 如果没有 conversations，清空状态
                    setCurrentConversationId(null);
                    setMessages([]);
                }
            } catch (error) {
                console.error("Error loading dashboard conversations:", error);
                setConversations([]);
                setCurrentConversationId(null);
                setMessages([]);
            } finally {
                setIsLoadingConversations(false);
            }
        };

        loadDashboardConversations();
    }, [selectedVersion?.id, getMessages]); // 只依赖 dashboard ID 和 getMessages

    const addMessage = useCallback(async (message: MessageType) => {
        setMessages(prevMessages => [...prevMessages, message]);

        // Proactively update dashboard state for better responsiveness
        // This ensures loading states and data updates appear immediately without waiting for the full schema rebuild
        const dashboardId = selectedVersion?.id;
        if (!dashboardId) return;

        if (message.event_type === EVENT_TYPES.TOOL_COMPLETED) {
            const toolMsg = message as any;
            const result = toolMsg.output_struct?.success ? toolMsg.output_struct.result : {};
            const id = result.id;
            const action = result.action || 'add';

            if (!id) return;

            if (toolMsg.tool_name === 'kpi') {
                const newKpi: KPI = {
                    id: id,
                    title: 'New KPI',
                    sql: '',
                    formula: '',
                    column: '',
                    format: 'number',
                    unit: '',
                    data: [],
                    loading: false,
                    ...result
                };

                let op: OperationType = 'ADD';
                let path = 'kpi';

                if (action === 'update' || action === 'delete') {
                    const currentSchema = editorStates[dashboardId]?.currentSchema;
                    const idx = currentSchema?.kpi?.findIndex((k: any) => k.id === id) ?? -1;

                    if (idx !== -1) {
                        path = `kpi[${idx}]`;
                        op = action === 'delete' ? 'DELETE' : 'UPDATE';
                    } else {
                        // If updating but KPI not found, log warning and skip
                        // This prevents duplicate KPIs with same ID
                        if (action === 'update') {
                            console.warn(`KPI with id ${id} not found for update, skipping to avoid duplicate`);
                            return;
                        }
                        // For delete, just skip silently
                        return;
                    }
                }

                // DELETE 操作使用批量队列
                if (op === 'DELETE') {
                    setActiveItemId(id);

                    // 添加到批量删除队列
                    if (!pendingDeleteQueue.current.has(dashboardId)) {
                        pendingDeleteQueue.current.set(dashboardId, []);
                    }
                    pendingDeleteQueue.current.get(dashboardId)!.push({ id, type: 'kpi' });

                    // 清除之前的定时器
                    const existingTimer = deleteTimerRef.current.get(dashboardId);
                    if (existingTimer) {
                        clearTimeout(existingTimer);
                    }

                    // 设置新的定时器，500ms 后批量处理
                    const timer = setTimeout(() => {
                        const queue = pendingDeleteQueue.current.get(dashboardId);
                        if (!queue || queue.length === 0) return;

                        console.log('批量处理删除操作:', queue);

                        // 按类型分组并计算索引
                        const kpiDeletes: Array<{ id: string; idx: number }> = [];
                        const chartDeletes: Array<{ id: string; idx: number }> = [];
                        const groupDeletes: Array<{ id: string; idx: number }> = [];

                        const currentSchema = editorStatesRef.current[dashboardId]?.currentSchema;
                        if (!currentSchema) return;

                        queue.forEach(({ id, type }) => {
                            if (type === 'kpi') {
                                const idx = currentSchema.kpi?.findIndex((k: any) => k.id === id) ?? -1;
                                if (idx !== -1) kpiDeletes.push({ id, idx });
                            } else if (type === 'chart') {
                                const idx = currentSchema.chart?.findIndex((c: any) => c.id === id) ?? -1;
                                if (idx !== -1) chartDeletes.push({ id, idx });
                            } else if (type === 'group') {
                                const idx = currentSchema.group?.findIndex((g: any) => g.id === id) ?? -1;
                                if (idx !== -1) groupDeletes.push({ id, idx });
                            }
                        });

                        // 按索引从大到小排序，避免删除时索引变化
                        kpiDeletes.sort((a, b) => b.idx - a.idx);
                        chartDeletes.sort((a, b) => b.idx - a.idx);
                        groupDeletes.sort((a, b) => b.idx - a.idx);

                        // 构建批量删除操作
                        const ops: PatchOperation[] = [];
                        kpiDeletes.forEach(({ idx }) => ops.push({ op: 'DELETE', path: `kpi[${idx}]` }));
                        chartDeletes.forEach(({ idx }) => ops.push({ op: 'DELETE', path: `chart[${idx}]` }));
                        groupDeletes.forEach(({ idx }) => ops.push({ op: 'DELETE', path: `group[${idx}]` }));

                        // 收集要删除的 items ID
                        const deletedItemIds = new Set([
                            ...kpiDeletes.map(d => d.id),
                            ...chartDeletes.map(d => d.id)
                        ]);

                        // 收集要删除的 group ID
                        const deletedGroupIds = new Set(groupDeletes.map(d => d.id));

                        // 遍历现有的 groups，移除已删除的 items
                        if (currentSchema.group && deletedItemIds.size > 0) {
                            currentSchema.group.forEach((group: any, groupIndex: number) => {
                                // 如果该 group 本身也要被删除，则跳过
                                if (deletedGroupIds.has(group.id)) return;

                                // 检查该 group 是否包含要删除的 item
                                if (group.items_id && group.items_id.some((itemId: string) => deletedItemIds.has(itemId))) {
                                    const newItemsId = group.items_id.filter((itemId: string) => !deletedItemIds.has(itemId));
                                    ops.push({
                                        op: 'UPDATE',
                                        path: `group[${groupIndex}].items_id`,
                                        value: newItemsId
                                    });
                                }
                            });
                        }

                        // 一次性 dispatch 所有删除操作
                        if (ops.length > 0) {
                            // 如果删除操作涉及分组，需要清空所有 layout 让系统重新计算
                            const finalOps = [...ops];

                            if (groupDeletes.length > 0) {
                                // 清空所有 kpi 的 layout
                                if (currentSchema.kpi) {
                                    currentSchema.kpi.forEach((kpi: any, idx: number) => {
                                        if (kpi.layout) {
                                            finalOps.push({
                                                op: 'UPDATE',
                                                path: `kpi[${idx}].layout`,
                                                value: { w: kpi.layout.w, h: kpi.layout.h } // 保留宽高，清空位置
                                            });
                                        }
                                    });
                                }

                                // 清空所有 chart 的 layout
                                if (currentSchema.chart) {
                                    currentSchema.chart.forEach((chart: any, idx: number) => {
                                        if (chart.layout) {
                                            finalOps.push({
                                                op: 'UPDATE',
                                                path: `chart[${idx}].layout`,
                                                value: { w: chart.layout.w, h: chart.layout.h } // 保留宽高，清空位置
                                            });
                                        }
                                    });
                                }
                            }

                            dispatchAction(dashboardId, {
                                type: 'BATCH_PATCH',
                                payload: finalOps,
                                description: 'Batch DELETE'
                            });
                        }

                        // 清空队列
                        pendingDeleteQueue.current.set(dashboardId, []);
                        deleteTimerRef.current.delete(dashboardId);
                    }, 500);

                    deleteTimerRef.current.set(dashboardId, timer);
                } else {
                    setActiveItemId(id);
                    dispatchAction(dashboardId, {
                        type: 'BATCH_PATCH',
                        payload: [{
                            op: op,
                            path: path,
                            value: newKpi
                        }],
                        description: `KPI ${action.toUpperCase()}`
                    });
                }
            } else if (toolMsg.tool_name === 'chart') {
                const newChart: Chart = {
                    id: id,
                    title: 'New Chart',
                    sql: '',
                    formula: '',
                    data: [],
                    chart_config: {},
                    loading: false,
                    ...result
                };

                let op: OperationType = 'ADD';
                let path = 'chart';

                if (action === 'update' || action === 'delete') {
                    const currentSchema = editorStates[dashboardId]?.currentSchema;
                    const idx = currentSchema?.chart?.findIndex((c: any) => c.id === id) ?? -1;

                    if (idx !== -1) {
                        path = `chart[${idx}]`;
                        op = action === 'delete' ? 'DELETE' : 'UPDATE';
                    } else {
                        // If updating but chart not found, log warning and skip
                        // This prevents duplicate charts with same ID
                        if (action === 'update') {
                            console.warn(`Chart with id ${id} not found for update, skipping to avoid duplicate`);
                            return;
                        }
                        // For delete, just skip silently
                        return;
                    }
                }

                // DELETE 操作使用批量队列
                if (op === 'DELETE') {
                    setActiveItemId(id);

                    // 添加到批量删除队列
                    if (!pendingDeleteQueue.current.has(dashboardId)) {
                        pendingDeleteQueue.current.set(dashboardId, []);
                    }
                    pendingDeleteQueue.current.get(dashboardId)!.push({ id, type: 'chart' });

                    // 清除之前的定时器
                    const existingTimer = deleteTimerRef.current.get(dashboardId);
                    if (existingTimer) {
                        clearTimeout(existingTimer);
                    }

                    // 设置新的定时器，500ms 后批量处理
                    const timer = setTimeout(() => {
                        const queue = pendingDeleteQueue.current.get(dashboardId);
                        if (!queue || queue.length === 0) return;

                        console.log('批量处理删除操作:', queue);

                        // 按类型分组并计算索引
                        const kpiDeletes: Array<{ id: string; idx: number }> = [];
                        const chartDeletes: Array<{ id: string; idx: number }> = [];
                        const groupDeletes: Array<{ id: string; idx: number }> = [];

                        const currentSchema = editorStatesRef.current[dashboardId]?.currentSchema;
                        if (!currentSchema) return;

                        queue.forEach(({ id, type }) => {
                            if (type === 'kpi') {
                                const idx = currentSchema.kpi?.findIndex((k: any) => k.id === id) ?? -1;
                                if (idx !== -1) kpiDeletes.push({ id, idx });
                            } else if (type === 'chart') {
                                const idx = currentSchema.chart?.findIndex((c: any) => c.id === id) ?? -1;
                                if (idx !== -1) chartDeletes.push({ id, idx });
                            } else if (type === 'group') {
                                const idx = currentSchema.group?.findIndex((g: any) => g.id === id) ?? -1;
                                if (idx !== -1) groupDeletes.push({ id, idx });
                            }
                        });

                        // 按索引从大到小排序，避免删除时索引变化
                        kpiDeletes.sort((a, b) => b.idx - a.idx);
                        chartDeletes.sort((a, b) => b.idx - a.idx);
                        groupDeletes.sort((a, b) => b.idx - a.idx);

                        // 构建批量删除操作
                        const ops: PatchOperation[] = [];
                        kpiDeletes.forEach(({ idx }) => ops.push({ op: 'DELETE', path: `kpi[${idx}]` }));
                        chartDeletes.forEach(({ idx }) => ops.push({ op: 'DELETE', path: `chart[${idx}]` }));
                        groupDeletes.forEach(({ idx }) => ops.push({ op: 'DELETE', path: `group[${idx}]` }));

                        // 收集要删除的 items ID
                        const deletedItemIds = new Set([
                            ...kpiDeletes.map(d => d.id),
                            ...chartDeletes.map(d => d.id)
                        ]);

                        // 收集要删除的 group ID
                        const deletedGroupIds = new Set(groupDeletes.map(d => d.id));

                        // 遍历现有的 groups，移除已删除的 items
                        if (currentSchema.group && deletedItemIds.size > 0) {
                            currentSchema.group.forEach((group: any, groupIndex: number) => {
                                // 如果该 group 本身也要被删除，则跳过
                                if (deletedGroupIds.has(group.id)) return;

                                // 检查该 group 是否包含要删除的 item
                                if (group.items_id && group.items_id.some((itemId: string) => deletedItemIds.has(itemId))) {
                                    const newItemsId = group.items_id.filter((itemId: string) => !deletedItemIds.has(itemId));
                                    ops.push({
                                        op: 'UPDATE',
                                        path: `group[${groupIndex}].items_id`,
                                        value: newItemsId
                                    });
                                }
                            });
                        }

                        // 一次性 dispatch 所有删除操作
                        if (ops.length > 0) {
                            // 如果删除操作涉及分组，需要清空所有 layout 让系统重新计算
                            const finalOps = [...ops];

                            if (groupDeletes.length > 0) {
                                // 清空所有 kpi 的 layout
                                if (currentSchema.kpi) {
                                    currentSchema.kpi.forEach((kpi: any, idx: number) => {
                                        if (kpi.layout) {
                                            finalOps.push({
                                                op: 'UPDATE',
                                                path: `kpi[${idx}].layout`,
                                                value: { w: kpi.layout.w, h: kpi.layout.h } // 保留宽高，清空位置
                                            });
                                        }
                                    });
                                }

                                // 清空所有 chart 的 layout
                                if (currentSchema.chart) {
                                    currentSchema.chart.forEach((chart: any, idx: number) => {
                                        if (chart.layout) {
                                            finalOps.push({
                                                op: 'UPDATE',
                                                path: `chart[${idx}].layout`,
                                                value: { w: chart.layout.w, h: chart.layout.h } // 保留宽高，清空位置
                                            });
                                        }
                                    });
                                }
                            }

                            dispatchAction(dashboardId, {
                                type: 'BATCH_PATCH',
                                payload: finalOps,
                                description: 'Batch DELETE'
                            });
                        }

                        // 清空队列
                        pendingDeleteQueue.current.set(dashboardId, []);
                        deleteTimerRef.current.delete(dashboardId);
                    }, 500);

                    deleteTimerRef.current.set(dashboardId, timer);
                } else {
                    setActiveItemId(id);
                    dispatchAction(dashboardId, {
                        type: 'BATCH_PATCH',
                        payload: [{
                            op: op,
                            path: path,
                            value: newChart
                        }],
                        description: `Chart ${action.toUpperCase()}`
                    });
                }
            } else if (toolMsg.tool_name === 'group') {
                const newGroup: Group = {
                    id: id,
                    title: result.title || 'New Section',
                    items_id: result.items_id || [],
                    ...result
                };

                let op: OperationType = 'ADD';
                let path = 'group';

                if (action === 'update' || action === 'delete') {
                    const currentSchema = editorStates[dashboardId]?.currentSchema;
                    const idx = currentSchema?.group?.findIndex((g: any) => g.id === id) ?? -1;

                    if (idx !== -1) {
                        path = `group[${idx}]`;
                        op = action === 'delete' ? 'DELETE' : 'UPDATE';
                    } else {
                        if (action === 'update') {
                            console.warn(`Group with id ${id} not found for update, skipping to avoid duplicate`);
                            return;
                        }
                        return;
                    }
                }

                // DELETE 操作使用批量队列
                if (op === 'DELETE') {
                    setActiveItemId(id);

                    // 添加到批量删除队列
                    if (!pendingDeleteQueue.current.has(dashboardId)) {
                        pendingDeleteQueue.current.set(dashboardId, []);
                    }
                    pendingDeleteQueue.current.get(dashboardId)!.push({ id, type: 'group' });

                    // 清除之前的定时器
                    const existingTimer = deleteTimerRef.current.get(dashboardId);
                    if (existingTimer) {
                        clearTimeout(existingTimer);
                    }

                    // 设置新的定时器，500ms 后批量处理
                    const timer = setTimeout(() => {
                        const queue = pendingDeleteQueue.current.get(dashboardId);
                        if (!queue || queue.length === 0) return;

                        console.log('批量处理删除操作:', queue);

                        // 按类型分组并计算索引
                        const kpiDeletes: Array<{ id: string; idx: number }> = [];
                        const chartDeletes: Array<{ id: string; idx: number }> = [];
                        const groupDeletes: Array<{ id: string; idx: number }> = [];

                        const currentSchema = editorStatesRef.current[dashboardId]?.currentSchema;
                        if (!currentSchema) return;

                        queue.forEach(({ id, type }) => {
                            if (type === 'kpi') {
                                const idx = currentSchema.kpi?.findIndex((k: any) => k.id === id) ?? -1;
                                if (idx !== -1) kpiDeletes.push({ id, idx });
                            } else if (type === 'chart') {
                                const idx = currentSchema.chart?.findIndex((c: any) => c.id === id) ?? -1;
                                if (idx !== -1) chartDeletes.push({ id, idx });
                            } else if (type === 'group') {
                                const idx = currentSchema.group?.findIndex((g: any) => g.id === id) ?? -1;
                                if (idx !== -1) groupDeletes.push({ id, idx });
                            }
                        });

                        // 按索引从大到小排序，避免删除时索引变化
                        kpiDeletes.sort((a, b) => b.idx - a.idx);
                        chartDeletes.sort((a, b) => b.idx - a.idx);
                        groupDeletes.sort((a, b) => b.idx - a.idx);

                        // 构建批量删除操作
                        const ops: PatchOperation[] = [];
                        kpiDeletes.forEach(({ idx }) => ops.push({ op: 'DELETE', path: `kpi[${idx}]` }));
                        chartDeletes.forEach(({ idx }) => ops.push({ op: 'DELETE', path: `chart[${idx}]` }));
                        groupDeletes.forEach(({ idx }) => ops.push({ op: 'DELETE', path: `group[${idx}]` }));

                        // 收集要删除的 items ID
                        const deletedItemIds = new Set([
                            ...kpiDeletes.map(d => d.id),
                            ...chartDeletes.map(d => d.id)
                        ]);

                        // 收集要删除的 group ID
                        const deletedGroupIds = new Set(groupDeletes.map(d => d.id));

                        // 遍历现有的 groups，移除已删除的 items
                        if (currentSchema.group && deletedItemIds.size > 0) {
                            currentSchema.group.forEach((group: any, groupIndex: number) => {
                                // 如果该 group 本身也要被删除，则跳过
                                if (deletedGroupIds.has(group.id)) return;

                                // 检查该 group 是否包含要删除的 item
                                if (group.items_id && group.items_id.some((itemId: string) => deletedItemIds.has(itemId))) {
                                    const newItemsId = group.items_id.filter((itemId: string) => !deletedItemIds.has(itemId));
                                    ops.push({
                                        op: 'UPDATE',
                                        path: `group[${groupIndex}].items_id`,
                                        value: newItemsId
                                    });
                                }
                            });
                        }

                        // 一次性 dispatch 所有删除操作
                        if (ops.length > 0) {
                            // 如果删除操作涉及分组，需要清空所有 layout 让系统重新计算
                            const finalOps = [...ops];

                            if (groupDeletes.length > 0) {
                                // 清空所有 kpi 的 layout
                                if (currentSchema.kpi) {
                                    currentSchema.kpi.forEach((kpi: any, idx: number) => {
                                        if (kpi.layout) {
                                            finalOps.push({
                                                op: 'UPDATE',
                                                path: `kpi[${idx}].layout`,
                                                value: { w: kpi.layout.w, h: kpi.layout.h } // 保留宽高，清空位置
                                            });
                                        }
                                    });
                                }

                                // 清空所有 chart 的 layout
                                if (currentSchema.chart) {
                                    currentSchema.chart.forEach((chart: any, idx: number) => {
                                        if (chart.layout) {
                                            finalOps.push({
                                                op: 'UPDATE',
                                                path: `chart[${idx}].layout`,
                                                value: { w: chart.layout.w, h: chart.layout.h } // 保留宽高，清空位置
                                            });
                                        }
                                    });
                                }
                            }

                            dispatchAction(dashboardId, {
                                type: 'BATCH_PATCH',
                                payload: finalOps,
                                description: 'Batch DELETE'
                            });
                        }

                        // 清空队列
                        pendingDeleteQueue.current.set(dashboardId, []);
                        deleteTimerRef.current.delete(dashboardId);
                    }, 500);

                    deleteTimerRef.current.set(dashboardId, timer);
                } else {
                    setActiveItemId(id);

                    // 分组操作需要清空所有 layout，让系统重新计算紧凑布局
                    const ops: PatchOperation[] = [];
                    const currentSchema = editorStates[dashboardId]?.currentSchema;

                    // 添加分组操作
                    ops.push({
                        op: op,
                        path: path,
                        value: newGroup
                    });

                    // 清空所有 kpi 的 layout
                    if (currentSchema?.kpi) {
                        currentSchema.kpi.forEach((kpi: any, idx: number) => {
                            if (kpi.layout) {
                                ops.push({
                                    op: 'UPDATE',
                                    path: `kpi[${idx}].layout`,
                                    value: { w: kpi.layout.w, h: kpi.layout.h } // 保留宽高，清空位置
                                });
                            }
                        });
                    }

                    // 清空所有 chart 的 layout
                    if (currentSchema?.chart) {
                        currentSchema.chart.forEach((chart: any, idx: number) => {
                            if (chart.layout) {
                                ops.push({
                                    op: 'UPDATE',
                                    path: `chart[${idx}].layout`,
                                    value: { w: chart.layout.w, h: chart.layout.h } // 保留宽高，清空位置
                                });
                            }
                        });
                    }

                    dispatchAction(dashboardId, {
                        type: 'BATCH_PATCH',
                        payload: ops,
                        description: `Group ${action.toUpperCase()}`
                    });
                }
            }
        }
    }, [dispatchAction, selectedVersion?.id, editorStates]);

    const cancelCurrentRequest = useCallback(() => {
        if (abortControllerRef.current && isGenerating) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
            addMessage({
                conversation_id: `dashboard_${Date.now()}`,
                created_at: new Date().toISOString(),
                event_type: EVENT_TYPES.SYSTEM_MESSAGE_DELTA,
                role: ROLES.SYSTEM,
                status: STATUSES.STREAMING,
                agent_type: AGENT_TYPE.AGENT_MODE,
                delta: '',
                content: 'Dashboard generation cancelled'
            });
            setIsGenerating(false);
        }
    }, [isGenerating, addMessage]);

    const getDashboards = useCallback(async () => {
        if (!workspaceId) return;
        setIsLoadingVersions(true);
        try {
            if (DISABLE_WORKSPACE_DASHBOARD_API) {
                // workspace_dashboard API is being sunset. Keep dashboard list empty and skip remote fetch.
                // Keep Supabase user hydration call so auth/user-dependent UI (e.g. avatar) behavior is unchanged.
                await supabase.auth.getUser();
                setVersions([]);
                setSelectedVersion(null);
                return;
            }
            let { data, error } = await supabase
                .from('workspace_dashboard')
                .select('*')
                .eq('workspace_id', workspaceId)
                .order('sort_key', { ascending: false });
            if (error) {
                console.error('Error fetching dashboards:', error);
                setIsLoadingVersions(false);
                return;
            }

            const defaultRange = {
                startDate: dayjs().subtract(30, 'day').toDate(),
                endDate: dayjs().toDate(),
            };
            const mapped: Dashboard[] = (data || []).map((row: any) => ({
                id: String(row.id),
                title: row.title || 'Dashboard',
                schema: typeof row.schema === 'string' ? JSON.parse(row.schema) : row.schema,
                created_at: row.created_at,
                dateRange: defaultRange,
                conversation_id: row.conversation_id || undefined,
            }));
            setVersions(mapped);
            setSelectedVersion(mapped[0] || null);
        }catch(e){
            console.error('Error fetching dashboards:', e);
        } finally {
            setIsLoadingVersions(false);
        }
    }, [workspaceId]);

    const deleteDashboard = useCallback(async (id: string) => {
        if (DISABLE_WORKSPACE_DASHBOARD_API) {
            // workspace_dashboard API is being sunset. Keep local delete only.
            setVersions(prev => prev.filter(v => v.id !== id));
            if (selectedVersion?.id === id) {
                setVersions(prev => {
                    const next = prev.length > 0 ? prev[0] : null;
                    setSelectedVersion(next);
                    return prev;
                });
            }
            return;
        }
        try {
            const { error } = await supabase
                .from('workspace_dashboard')
                .delete()
                .eq('id', id);
            
            if (error) throw error;
            
            setVersions(prev => prev.filter(v => v.id !== id));
            if (selectedVersion?.id === id) {
                // Select first available or null
                setVersions(prev => {
                    const next = prev.length > 0 ? prev[0] : null;
                    setSelectedVersion(next);
                    return prev;
                });
            }
        } catch (error) {
            console.error("Error deleting dashboard:", error);
        }
    }, [selectedVersion]);

    const renameDashboard = useCallback(async (id: string, newName: string) => {
        if (DISABLE_WORKSPACE_DASHBOARD_API) {
            // workspace_dashboard API is being sunset. Keep local rename only.
            setVersions(prev => prev.map(v => v.id === id ? { ...v, title: newName } : v));
            if (selectedVersion?.id === id) {
                setSelectedVersion(prev => prev ? { ...prev, title: newName } : prev);
            }
            return;
        }
        try {
            const { error } = await supabase
                .from('workspace_dashboard')
                .update({ title: newName })
                .eq('id', id);
            
            if (error) throw error;
            
            setVersions(prev => prev.map(v => v.id === id ? { ...v, title: newName } : v));
            if (selectedVersion?.id === id) {
                setSelectedVersion(prev => prev ? { ...prev, title: newName } : prev);
            }
        } catch (error) {
            console.error("Error renaming dashboard:", error);
        }
    }, [selectedVersion]);

    const reorderDashboards = useCallback(async (newOrder: Dashboard[], movedId?: string, newIndex?: number) => {
        // Optimistic update
        setVersions(newOrder);
        
        if (!movedId || newIndex === undefined || newIndex === null) {
            console.warn("Missing movedId or newIndex for reorder, fallback to full reorder not implemented");
            return;
        }

        if (DISABLE_WORKSPACE_DASHBOARD_API) {
            // workspace_dashboard API is being sunset. Keep local reorder only.
            return;
        }

        // Calculate new sort_key
        // Note: newOrder is ALREADY sorted by the user's drag.
        // But the sort_keys inside are stale (from before the drag).
        
        const prevItem = newOrder[newIndex - 1];
        const nextItem = newOrder[newIndex + 1];
        
        // We assume Descending order (higher key at top)
        // If moved to Top (index 0), newKey > nextItem.key
        // If moved to Bottom (index N), newKey < prevItem.key
        // If Middle, between prev and next.
        
        // Safety checks for keys. If undefined, we might need fallback.
        // Assuming all items have sort_key populated from DB.
        
        let newSortKey: number;
        const GAP = 1000000;

        if (!prevItem) {
            // Top of list
            // Use Date.now() to ensure it's larger than any existing timestamp-based keys (assuming no future keys)
            // But if we have mixed keys (some small, some timestamps), we should be careful.
            // If the nextItem key is HUGE (future timestamp?), Date.now() might not be enough.
            // Safer: max(Date.now(), nextKey + GAP)
            const nextKey = nextItem?.sort_key ?? 0;
            newSortKey = Math.max(Date.now(), nextKey + GAP);
        } else if (!nextItem) {
            // Bottom of list
            const prevKey = prevItem?.sort_key ?? 0;
            newSortKey = prevKey - GAP;
        } else {
            // Between two items
            const prevKey = prevItem.sort_key || 0;
            const nextKey = nextItem.sort_key || 0;
            
            // If keys are 0 (e.g. old data), we need to handle it gracefully.
            // But if both are 0, we can't really insert between.
            // We should assume if keys are missing/zero, we might need a reset strategy or fallback.
            // For now, if we get 0, let's assume it's just a small number.
            
            if (prevKey === 0 && nextKey === 0) {
                // Edge case: all keys are 0 or null.
                // We should probably just give it a new large timestamp-based key to force it to top/bottom?
                // Or just 0 is fine if we don't care.
                // But wait, if we are in middle, and both neighbors are 0, new key is 0.
                // That means collision.
                // Ideally we should re-index everything if we detect this degeneration.
                // Fallback:
                newSortKey = Date.now();
            } else {
                newSortKey = (prevKey + nextKey) / 2;
            }
        }

        // Update local state sort_key to prevent future jitter/inconsistency
        // Clone to avoid mutation of state objects
        const updatedItem = { ...newOrder[newIndex], sort_key: newSortKey };
        const newOrderWithUpdate = [...newOrder];
        newOrderWithUpdate[newIndex] = updatedItem;
        
        // Force update versions with new key
        setVersions(newOrderWithUpdate); 

        try {
            const { error } = await supabase
                .from('workspace_dashboard')
                .update({ sort_key: newSortKey })
                .eq('id', movedId);
                
            if (error) {
                console.error("Supabase update error:", error);
                throw error;
            }

        } catch (e) {
            console.error("Failed to reorder dashboards:", e);
            getDashboards();
        }
    }, []);

    const _showCreateModal = (show: boolean) => {
        if(show && !isGenerating){
            setHistory(prev => [...prev, ...messages])
            setMessages([])
        }
        setShowCreateModal(show);
    }

    useEffect(()=>{
        if (workspaceId) {
            getDashboards()
        }
    }, [workspaceId, getDashboards])

  
    return {
        messages,
        setMessages,
        addMessage,
        isGenerating,
        setIsGenerating,
        showGenerating,
        setShowGenerating,
        abortControllerRef,
        cancelCurrentRequest,
        createNewAbortController,
        error,
        setError,
        selectedVersion,
        setSelectedVersion,
        versions,
        setVersions,
        history,
        setHistory,
        isLoadingVersions,
        setIsLoadingVersions,
        
        editorStates,
        initEditor,
        dispatchAction,
        undo,
        redo,
        saveDashboard,
        resetEditor,
        updateData,
        deleteDashboard,
        renameDashboard,
        reorderDashboards,

        showCreateModal,
        setShowCreateModal: _showCreateModal,
        getDashboards,
        conversations,
        setConversations,
        currentConversationId,
        setCurrentConversationId,
        isLoadingConversations,
        isLoadingMessages,
        getConversations,
        createConversation,
        deleteConversation,
        updateConversation,
        getMessages,
        isEditMode,
        setIsEditMode,
        localSchema,
        setLocalSchema,
        globalDateRange,
        setGlobalDateRange,
        activeItemId,
        setActiveItemId
    };
};
